import { floatTwo, pxToRem } from '@shared/Frontend/utils/pxRem'
import { StyleguideTokenPart, StyleguideTokenRow } from '@shared/Hoppinger.Styleguide/Frontend'
import * as React from 'react'

type TokensTypographyTypes = {
  prefix: string
  size: string
  description?: string
  fontSize: number
  fontSizeMobile?: number
  lineHeight: number
  lineHeightMobile?: number
  hasSeparator?: boolean
  extraAttributes: string[]
  extraClasses?: string
}

type TokensStyleTypes = {
  style: string
  value: string
  description?: string
  hasSeparator?: boolean
  prefix: string
  fontFamily: string
}

// ----- Font

export const typographyFontTokens = [
  {
    prefix: 'family',
    fontFamily: 'default',
    description: 'Body text',
    extraAttributes: ["'Open Sans'"],
    fallback: {
      name: 'opensans-fallback',
      description: "Fallback is based on 'Arial'",
      extraAttributes: ["'Open Sans-fallback'"]
    }
  },
  {
    prefix: 'family',
    fontFamily: 'title',
    description: 'Titles',
    extraAttributes: ["'Barlow'"],
    fallback: {
      name: 'barlow-fallback',
      description: "Fallback is based on 'Arial'",
      extraAttributes: ["'Barlow-fallback'"]
    }
  }
] as const

// ----- Typography

const fontTitles = "Font-family: 'Barlow', serif"
const fontText = "Font-family: 'TT Hoves', sans-serif"

export const typographyTitleTokens: TokensTypographyTypes[] = [
  {
    prefix: 'title',
    size: 'xl',
    fontSize: 48,
    lineHeight: 58,
    fontSizeMobile: 32,
    extraAttributes: [fontTitles]
  },
  {
    prefix: 'title',
    size: 'l',
    fontSize: 48,
    lineHeight: 58,
    fontSizeMobile: 32,
    extraAttributes: [fontTitles]
  },
  { prefix: 'title', size: 'm', fontSize: 24, lineHeight: 30, extraAttributes: [fontTitles] },
  { prefix: 'title', size: 's', fontSize: 20, lineHeight: 30, extraAttributes: [fontTitles], hasSeparator: true }
]
export const typographyTextTokens: TokensTypographyTypes[] = [
  {
    prefix: 'text',
    size: 'l',
    fontSize: 18,
    lineHeight: 30,
    fontSizeMobile: 16,
    extraAttributes: [fontText]
  },
  { prefix: 'text', size: 's', fontSize: 14, lineHeight: 24, extraAttributes: [fontText] }
]
export const typographyOtherTokens: TokensTypographyTypes[] = [
  {
    prefix: 'quote',
    size: 'm',
    fontSize: 32,
    lineHeight: 44.8,
    fontSizeMobile: 24,
    extraAttributes: [fontText]
  },
  { prefix: 'tag', size: 'm', fontSize: 14, lineHeight: 24, extraAttributes: [fontText] }
]

const typographyTokens: TokensTypographyTypes[] = [
  ...typographyTitleTokens,
  ...typographyTextTokens,
  ...typographyOtherTokens
]

// ----- Styles

const typographyStyleTokens: TokensStyleTypes[] = [
  {
    style: 'bold',
    value: '700',
    prefix: typographyFontTokens[1].prefix,
    fontFamily: typographyFontTokens[1].fontFamily,
    description: "Only used with 'Barlow'-font"
  },
  {
    style: 'extra-bold',
    value: '800',
    prefix: typographyFontTokens[1].prefix,
    fontFamily: typographyFontTokens[1].fontFamily,
    description: "Only used with 'Barlow'-font"
  },
  {
    style: 'black',
    value: '900',
    prefix: typographyFontTokens[1].prefix,
    fontFamily: typographyFontTokens[1].fontFamily,
    description: "Only used with 'Barlow'-font",
    hasSeparator: true
  },
  {
    style: 'regular',
    value: '400',
    prefix: typographyFontTokens[0].prefix,
    fontFamily: typographyFontTokens[0].fontFamily,
    description: "Only used with 'Open Sans'-font"
  },
  {
    style: 'semibold',
    value: '600',
    prefix: typographyFontTokens[0].prefix,
    fontFamily: typographyFontTokens[0].fontFamily,
    description: "Only used with 'Open Sans'-font",
    hasSeparator: true
  },
  {
    style: 'italic',
    value: 'italic',
    prefix: typographyFontTokens[0].prefix,
    fontFamily: typographyFontTokens[0].fontFamily,
    description: "Only used with 'Open Sans'-font"
  }
]

// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ----------  STYLEGUIDE RENDERER  ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------

export const TokensFonts = () => (
  <StyleguideTokenPart hasAttributeColumn={true} hasExampleColumn={true}>
    <>
      {typographyFontTokens.map((font) => {
        return font.fallback ? (
          <React.Fragment key={font.fontFamily}>
            <StyleguideTokenRow
              code={`$${font.prefix}-${font.fontFamily}`}
              description={font.description}
              attributes={[...font.extraAttributes]}
              columnCount={3}
              hasSeparator={false}
              hasExample={true}
            >
              <p className={`token-${font.prefix}--${font.fontFamily}`}>
                Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
              </p>
            </StyleguideTokenRow>
            <StyleguideTokenRow
              code={`$${font.prefix}-${font.fallback.name}`}
              description={font.fallback.description}
              attributes={[...font.fallback.extraAttributes]}
              columnCount={3}
              hasSeparator={true}
              hasExample={true}
            >
              <div className="styleguide-token__font-fallback">
                <p className={`token-${font.prefix}--${font.fontFamily}`}>
                  Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                </p>

                <p className={`token-${font.prefix}--${font.fallback?.name}`}>
                  Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
                </p>
              </div>
            </StyleguideTokenRow>
          </React.Fragment>
        ) : (
          <StyleguideTokenRow
            key={font.fontFamily}
            code={`$${font.prefix}-${font.fontFamily}`}
            description={font.description}
            attributes={[...font.extraAttributes]}
            columnCount={3}
            hasSeparator={false}
            hasExample={true}
          >
            <p className={`token-${font.prefix}--${font.fontFamily}`}>
              Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
            </p>
          </StyleguideTokenRow>
        )
      })}
    </>
  </StyleguideTokenPart>
)

export const TokensStyles = () => (
  <StyleguideTokenPart hasAttributeColumn={true} hasExampleColumn={true}>
    {typographyStyleTokens.map((style) => {
      return (
        <StyleguideTokenRow
          key={style.style}
          code={`${style.value}`}
          description={style.description || undefined}
          attributes={`${style.value}`}
          columnCount={3}
          hasSeparator={style.hasSeparator || false}
          hasExample={true}
        >
          <p
            className={`token-${style.prefix}--${style.fontFamily} token-${style.style == 'italic' ? 'style' : 'weight'}--${
              style.style
            }`}
          >
            Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.
          </p>
        </StyleguideTokenRow>
      )
    })}
  </StyleguideTokenPart>
)

export const TokensTypography = () => (
  <StyleguideTokenPart hasAttributeColumn={true} hasExampleColumn={true}>
    {typographyTokens.map((t, i) => {
      return (
        <StyleguideTokenRow
          key={i}
          code={`%${t.prefix}--${t.size}`}
          description={t.description || undefined}
          attributes={[
            t.fontSizeMobile
              ? `Font-size: ${t.fontSizeMobile}px (${pxToRem(t.fontSizeMobile)}) > ${t.fontSize}px (${pxToRem(
                  t.fontSize
                )})`
              : `Font-size: ${t.fontSize}px (${pxToRem(t.fontSize)})`,
            t.fontSizeMobile && t.lineHeightMobile
              ? `Line-height: ${t.lineHeightMobile}px (${pxToRem(t.lineHeightMobile)}) >  ${
                  t.lineHeight
                }px / ${floatTwo(t.lineHeightMobile / t.fontSizeMobile)} > ${floatTwo(t.lineHeight / t.fontSize)}`
              : `Line-height: ${t.lineHeight}px / ${floatTwo(t.lineHeight / t.fontSize)}`,
            ...t.extraAttributes
          ]}
          columnCount={3}
          hasSeparator={t.hasSeparator || false}
          hasExample={true}
        >
          <p className={`${t.prefix}--${t.size}${t.extraClasses ? ` ${t.extraClasses}` : ''}`}>
            {t.fontSize >= 20
              ? 'Vulputate Commodo Sollicitudin Fringilla Nullam'
              : 'Vulputate Commodo Sollicitudin Fringilla Nullam. Etiam porta sem malesuada magna mollis euismod.'}
          </p>
        </StyleguideTokenRow>
      )
    })}
  </StyleguideTokenPart>
)
